import axios from 'axios';
import { Website, WebsiteStatus, BackupStatus, MinioConfig, MinioBucket, MinioBucketReport, BackupSlot } from '../types';
import authService from './authService';

// Configuration de base pour axios
// En production, utilise une URL relative, en développement utilise localhost
const API_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api'
);

// Debug: afficher l'URL utilisée (à supprimer en production)
// console.log('API_URL configurée:', API_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Options supplémentaires pour CORS
  withCredentials: true
});

// Intercepteur pour ajouter automatiquement le token d'authentification
api.interceptors.request.use(
  config => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    
    // Si l'erreur est 401 (non autorisé), déconnecter l'utilisateur
    if (error.response?.status === 401) {
      authService.logout();
      // Rediriger vers la page de connexion
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Services pour les sites web
export const websiteService = {
  // Récupérer tous les sites
  getAll: async (): Promise<Website[]> => {
    try {
      const response = await api.get('/websites');
      return response.data;
    } catch (error) {
      console.error('Erreur dans getAll:', error);
      throw error;
    }
  },

  // Récupérer un site par son ID
  getById: async (id: string): Promise<Website> => {
    const response = await api.get(`/websites/${id}`);
    return response.data;
  },

  // Créer un nouveau site
  create: async (website: Omit<Website, 'id'>): Promise<Website> => {
    const response = await api.post('/websites', website);
    return response.data;
  },

  // Mettre à jour un site existant
  update: async (id: string, website: Website): Promise<Website> => {
    const response = await api.put(`/websites/${id}`, website);
    return response.data;
  },

  // Supprimer un site
  delete: async (id: string): Promise<void> => {
    await api.delete(`/websites/${id}`);
  },

  // Vérifier le statut d'un site
  checkStatus: async (id: string): Promise<WebsiteStatus> => {
    const response = await api.get(`/websites/${id}/check`);
    return response.data;
  },

  // Vérifier le statut de tous les sites
  checkAllStatus: async (): Promise<WebsiteStatus[]> => {
    const response = await api.get('/websites/check-all');
    return response.data;
  }
};

// Services pour les backups
export const backupService = {
  // Configurer la connexion Minio
  configureMinio: async (config: MinioConfig): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/backups/minio/configure', config);
    return response.data;
  },
  
  // Récupérer les statistiques de tous les buckets
  getAllBuckets: async (sortBy?: string): Promise<MinioBucket[]> => {
    const response = await api.get('/backups/minio/buckets', {
      params: sortBy ? { sort: sortBy } : undefined
    });
    return response.data.buckets;
  },
  
  // Vérifier les backups d'un site web
  checkWebsiteBackup: async (id: string): Promise<BackupStatus> => {
    const response = await api.get(`/backups/${id}/check`);
    return response.data;
  },
  
  // Vérifier les backups de tous les sites web
  checkAllBackups: async (): Promise<BackupStatus[]> => {
    const response = await api.get('/backups/check-all');
    return response.data;
  },
  
  // Générer un rapport sur les buckets Minio (utilise le cache si disponible)
  getMinioBucketReport: async (): Promise<MinioBucketReport> => {
    const response = await api.get('/backups/minio/report');
    return response.data;
  },
  
  // Forcer le rafraîchissement du rapport sur les buckets Minio
  refreshMinioBucketReport: async (): Promise<MinioBucketReport> => {
    const response = await api.get('/backups/minio/report/refresh');
    return response.data;
  },
  
  // Démarrer la génération de rapport avec progression
  startMinioBucketReportWithProgress: async (): Promise<{ taskId: string; progressUrl: string }> => {
    const response = await api.post('/backups/minio/report/start-progress');
    return response.data;
  },
  
  // Récupérer les tranches horaires de backup
  getBackupSlots: async (): Promise<BackupSlot[]> => {
    const response = await api.get('/backups/slots');
    return response.data.slots;
  },
  
  // Récupérer les tranches horaires disponibles
  getAvailableBackupSlots: async (): Promise<BackupSlot[]> => {
    const response = await api.get('/backups/slots/available');
    return response.data.availableSlots;
  },
  
  // Récupérer une tranche horaire par son numéro
  getBackupSlotByNumber: async (slotNumber: number): Promise<BackupSlot> => {
    const response = await api.get(`/backups/slots/${slotNumber}`);
    return response.data.slot;
  },
  
  // Assigner un site web à une tranche horaire
  assignWebsiteToSlot: async (websiteId: string, slotNumber: number): Promise<any> => {
    const response = await api.post('/backups/slots/assign', { websiteId, slotNumber });
    return response.data;
  },
  
  // Supprimer l'assignation d'un site web à une tranche horaire
  removeWebsiteFromSlot: async (websiteId: string): Promise<any> => {
    const response = await api.delete(`/backups/slots/website/${websiteId}`);
    return response.data;
  },
  
  // Récupérer la tranche horaire d'un site web
  getSlotForWebsite: async (websiteId: string): Promise<any> => {
    const response = await api.get(`/backups/slots/website/${websiteId}`);
    return response.data;
  },
  
  // Obtenir un rapport sur l'utilisation des tranches horaires
  getBackupSlotsReport: async (): Promise<any> => {
    const response = await api.get('/backups/slots/report');
    return response.data;
  },
  
  // Synchroniser les sites web avec leurs tranches horaires
  syncWebsitesWithBackupSlots: async (): Promise<any> => {
    const response = await api.get('/backups/slots/sync');
    return response.data;
  }
};

// Services pour la gestion des buckets MinIO (nouvelle API via McService)
export const bucketService = {
  // Lister tous les buckets avec leurs statistiques
  listBuckets: async (sortBy?: string, withStats: boolean = false): Promise<any> => {
    const params: any = {};
    if (sortBy) params.sort = sortBy;
    if (withStats) params.stats = 'true';
    
    const response = await api.get('/buckets', {
      params: Object.keys(params).length > 0 ? params : undefined
    });
    return response.data;
  },

  // Obtenir les détails d'un bucket spécifique
  getBucketDetails: async (bucketName: string): Promise<any> => {
    const response = await api.get(`/buckets/${bucketName}`);
    return response.data;
  },

  // Créer un nouveau bucket MinIO avec sa politique d'accès
  createBucket: async (bucketName: string, websiteId?: string): Promise<any> => {
    const response = await api.post('/buckets', { 
      bucketName,
      websiteId
    });
    return response.data;
  },

  // Supprimer un bucket MinIO et sa politique
  deleteBucket: async (bucketName: string, force = false): Promise<any> => {
    const response = await api.delete(`/buckets/${bucketName}`, {
      params: { force }
    });
    return response.data;
  },

  // Créer une politique d'accès pour un bucket
  createBucketPolicy: async (bucketName: string): Promise<any> => {
    const response = await api.post(`/buckets/${bucketName}/policy`);
    return response.data;
  },

  // Supprimer une politique d'accès pour un bucket
  deleteBucketPolicy: async (bucketName: string): Promise<any> => {
    const response = await api.delete(`/buckets/${bucketName}/policy`);
    return response.data;
  },

  // Créer automatiquement un bucket pour un site web
  createBucketForWebsite: async (websiteId: string): Promise<any> => {
    const response = await api.post(`/buckets/website/${websiteId}`);
    return response.data;
  },

  // Lister tous les utilisateurs MinIO
  listUsers: async (): Promise<any> => {
    const response = await api.get('/buckets/users');
    return response.data;
  },

  // Lister toutes les politiques MinIO
  listPolicies: async (): Promise<any> => {
    const response = await api.get('/buckets/policies');
    return response.data;
  },

  // Supprimer un utilisateur MinIO
  deleteUser: async (username: string): Promise<any> => {
    const response = await api.delete(`/buckets/users/${username}`);
    return response.data;
  },

  // Supprimer une politique MinIO
  deletePolicy: async (policyName: string): Promise<any> => {
    const response = await api.delete(`/buckets/policies/${policyName}`);
    return response.data;
  },

  // Lister tous les Service Accounts MinIO
  listServiceAccounts: async (): Promise<any> => {
    const response = await api.get('/buckets/service-accounts');
    return response.data;
  },

  // Supprimer un Service Account MinIO
  deleteServiceAccount: async (accessKey: string): Promise<any> => {
    const response = await api.delete(`/buckets/service-accounts/${accessKey}`);
    return response.data;
  },

  // Tester la connexion MinIO
  testConnection: async (): Promise<any> => {
    const response = await api.get('/buckets/test/connection');
    return response.data;
  }
};

// Services pour les sauvegardes journalières
export const dailyBackupService = {
  // Effectuer une sauvegarde manuelle
  performBackup: async (): Promise<any> => {
    const response = await api.post('/backups/daily/perform');
    return response.data;
  },
  
  // Effectuer une sauvegarde incrémentale
  performIncrementalBackup: async (): Promise<any> => {
    const response = await api.post('/backups/daily/incremental');
    return response.data;
  },
  
  // Récupérer la liste des sauvegardes
  getBackupList: async (): Promise<any> => {
    const response = await api.get('/backups/daily/list');
    return response.data;
  },
  
  // Récupérer le résumé des modifications
  getModificationSummary: async (): Promise<any> => {
    const response = await api.get('/backups/daily/summary');
    return response.data;
  },
  
  // Récupérer le résumé des modifications incrémentales
  getIncrementalSummary: async (): Promise<any> => {
    const response = await api.get('/backups/daily/incremental-summary');
    return response.data;
  },
  
  // Récupérer les statistiques de sauvegarde
  getBackupStats: async (): Promise<any> => {
    const response = await api.get('/backups/daily/stats');
    return response.data;
  },
  
  // Nettoyer les anciennes sauvegardes
  cleanOldBackups: async (retentionDays?: number): Promise<any> => {
    const params = retentionDays ? { retention: retentionDays } : {};
    const response = await api.delete('/backups/daily/cleanup', { params });
    return response.data;
  },
  
  // Récupérer l'état du planificateur
  getSchedulerStatus: async (): Promise<any> => {
    const response = await api.get('/backups/scheduler/status');
    return response.data;
  },
  
  // Démarrer le planificateur
  startScheduler: async (): Promise<any> => {
    const response = await api.post('/backups/scheduler/start');
    return response.data;
  },
  
  // Arrêter le planificateur
  stopScheduler: async (): Promise<any> => {
    const response = await api.post('/backups/scheduler/stop');
    return response.data;
  },
  
  // Modifier l'heure de sauvegarde
  setSchedulerTime: async (time: string): Promise<any> => {
    const response = await api.put('/backups/scheduler/time', { time });
    return response.data;
  },
  
  // Forcer une sauvegarde immédiate
  forceBackup: async (): Promise<any> => {
    const response = await api.post('/backups/scheduler/force');
    return response.data;
  }
};

// Services pour les sondes
export const probeService = {
  // Lister toutes les sondes disponibles
  listProbes: async () => {
    const response = await api.get('/probes');
    return response.data.probes;
  },
  
  // Exécuter toutes les sondes pour un site web
  detectAllForWebsite: async (id: string) => {
    const response = await api.get(`/probes/websites/${id}/detect`);
    return response.data;
  },
  
  // Exécuter une sonde spécifique pour un site web
  detectWithProbe: async (id: string, probeName: string) => {
    const response = await api.get(`/probes/websites/${id}/detect/${probeName}`);
    return response.data;
  },
  
  // Exécuter toutes les sondes pour tous les sites web
  detectForAllWebsites: async () => {
    const response = await api.get('/probes/websites/detect-all');
    return response.data;
  },
  
  // Exécuter une sonde spécifique pour tous les sites web
  detectProbeForAllWebsites: async (probeName: string) => {
    const response = await api.get(`/probes/websites/detect-all/${probeName}`);
    return response.data;
  },
  
  // Récupérer le rapport des sites MainWP non référencés dans la base de données
  getMainWPReport: async () => {
    const response = await api.get('/probes/mainwp/report');
    return response.data;
  },
  
  // Forcer le rafraîchissement du rapport MainWP
  refreshMainWPReport: async () => {
    const response = await api.get('/probes/mainwp/report/refresh');
    return response.data;
  },
  
  // Vider le cache MainWP pour forcer un rafraîchissement
  clearMainWPCache: async () => {
    const response = await api.post('/probes/mainwp/clear-cache');
    return response.data;
  },
  
  // Détecter les plugins WordPress d'un site
  detectWordPressPlugins: async (id: string) => {
    try {
      // D'abord vérifier si le site est WordPress avec la sonde wordpress
      const wordpressDetection = await api.get(`/probes/websites/${id}/detect/wordpress`);
      
      if (wordpressDetection.data.detectionResults.detected) {
        // Si c'est un site WordPress, exécuter la sous-sonde wp-plugins
        // La sous-sonde wp-plugins sera exécutée automatiquement dans la sonde WordPress
        // Les résultats seront dans detectionResults.subProbes['wp-plugins']
        if (wordpressDetection.data.detectionResults.subProbes && 
            wordpressDetection.data.detectionResults.subProbes['wp-plugins']) {
          return {
            ...wordpressDetection.data,
            pluginsResult: wordpressDetection.data.detectionResults.subProbes['wp-plugins']
          };
        } else {
          return {
            ...wordpressDetection.data,
            pluginsResult: { 
              detected: false, 
              error: "La sous-sonde wp-plugins n'a pas été exécutée ou n'a pas renvoyé de résultats" 
            }
          };
        }
      } else {
        return {
          ...wordpressDetection.data,
          pluginsResult: { 
            detected: false, 
            error: "Ce site n'est pas un site WordPress" 
          }
        };
      }
    } catch (error) {
      console.error('Erreur lors de la détection des plugins WordPress:', error);
      throw error;
    }
  }
};

// Services pour l'import/export
export const importExportService = {
  // Exporter les sites web
  exportWebsites: async (format: 'csv' | 'json' = 'csv') => {
    const response = await api.get('/websites/export', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Importer les sites web
  importWebsites: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/websites/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

export default api; 